import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { getAdressTypeOption } from "api";
import { useGetSeaports } from "api/queries/common/useGetSeaports";
import { useGetAirports } from "api/queries/common/useGetAirports";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import useSearchQuery from "hooks/useSearchQuery";
import useFeatureFlag from "hooks/useFeatureFlag";
import { StopAddressType } from "types/MultiStop";
import testIds from "constants/testIds";
import { FEATURE_FLAGS } from "constants/featureFlags";
import SearchSelect from "../../../Common/SearchSelect";

const mapPortData = (option: any) => ({
  id: option.id,
  label: option.name,
  value: option.name,
  city_name: option.city_name,
  country_name: option.country_name,
  country_code: option.country_code,
  code: option.un_locode || option.code,
  address: option.address,
});

export default function StopPortForm({
  index,
  registerStopPortLinkedTimezone,
}: {
  index: number;
  /** Callback receives port-linked IANA timezone from API `address`, or null. */
  registerStopPortLinkedTimezone?: (timezone: string | null) => void; // eslint-disable-line @typescript-eslint/no-unused-vars, no-unused-vars -- callback contract
}) {
  const intl = useIntl();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const { setValue, watch, getValues, control } = useFormContext();
  const clearSelectedAddress = useCallback(() => {
    setValue(`schedule.stops.${index}.address_id`, undefined);
    setValue(`schedule.stops.${index}.address_reference_number`, "");
    setValue(`schedule.stops.${index}.address_name`, "");
    setValue(`schedule.stops.${index}.street_address`, "");
    setValue(`schedule.stops.${index}.street_address_line_two`, "");
    setValue(`schedule.stops.${index}.city_name`, "");
    setValue(`schedule.stops.${index}.state_name`, "");
    setValue(`schedule.stops.${index}.country_name`, "");
    setValue(`schedule.stops.${index}.zipcode`, "");
    setValue(`schedule.stops.${index}.un_locode`, "");
    setValue(`schedule.stops.${index}.location_reference_number`, "");
    setValue(`schedule.stops.${index}.custom_address_reference`, "");
  }, [index, setValue]);
  const clearSelectedPort = useCallback(() => {
    setValue(`schedule.stops.${index}.stop_airport_name`, "");
    setValue(`schedule.stops.${index}.stop_airport_code`, "");
    setValue(`schedule.stops.${index}.stop_seaport_name`, "");
    setValue(`schedule.stops.${index}.stop_seaport_code`, "");
    setValue(`schedule.stops.${index}.port_country`, "");
    setValue(`schedule.stops.${index}.stop_timezone`, "");
    registerStopPortLinkedTimezone?.(null);
  }, [index, registerStopPortLinkedTimezone, setValue]);

  const isAddress = watch(`schedule.stops.${index}.stop_address_type`) === StopAddressType.ADDRESS;
  const isAirport = watch(`schedule.stops.${index}.stop_address_type`) === StopAddressType.AIRPORT;
  const isSeaport = watch(`schedule.stops.${index}.stop_address_type`) === StopAddressType.SEAPORT;

  const { query: airportQuery, handleInputChange: handleAirportInputChange } = useSearchQuery();
  const { query: seaportQuery, handleInputChange: handleSeaportInputChange } = useSearchQuery();

  const { data: airportsData = [] } = useGetAirports(airportQuery, {
    enabled: isAirport,
    select: (data): any => data.map((option) => mapPortData(option)),
  });
  const { data: seaportsData = [] } = useGetSeaports(seaportQuery, {
    enabled: isSeaport,
    select: (data): any => data.map((option) => mapPortData(option)),
  });

  const handleFillPortAddressOption = useCallback(
    (event, options: { preserveSelectedAddress?: boolean } = {}) => {
      const address = event[0];
      if (!address) return;
      if (!options.preserveSelectedAddress) {
        clearSelectedAddress();
      }
      if (getValues(`schedule.stops.${index}.stop_address_type`) === StopAddressType.SEAPORT) {
        setValue(`schedule.stops.${index}.stop_seaport_name`, address?.city_name);
        setValue(`schedule.stops.${index}.stop_seaport_code`, address?.code);
      } else if (getValues(`schedule.stops.${index}.stop_address_type`) === StopAddressType.AIRPORT) {
        setValue(`schedule.stops.${index}.stop_airport_name`, address?.city_name);
        setValue(`schedule.stops.${index}.stop_airport_code`, address?.code);
      }
      setValue(`schedule.stops.${index}.port_country`, address?.country_name || "");

      const linkedTimezone = address?.address?.timezone;
      if (isOptionalPortAddressFormEnabled && linkedTimezone) {
        setValue(`schedule.stops.${index}.stop_timezone`, linkedTimezone, { shouldValidate: true });
      }
      if (isOptionalPortAddressFormEnabled) {
        registerStopPortLinkedTimezone?.(address?.address?.timezone ?? null);
      }
    },
    [
      clearSelectedAddress,
      setValue,
      index,
      getValues,
      registerStopPortLinkedTimezone,
      isOptionalPortAddressFormEnabled,
    ],
  );

  // On initial mount of an edited / duplicated draft, the stop airport/seaport
  // code+name are prefilled but the SearchSelect does not treat them as a
  // selection. Re-resolve the prefilled code (or name) against the API and
  // simulate a selection to populate the linked address.
  //
  // NOTE: the form is hydrated asynchronously via methods.reset(...) AFTER
  // this component mounts. We MUST react to the prefilled fields appearing,
  // so we read them via useWatch instead of getValues-on-mount.
  const watchedStopAddressType = useWatch({ control, name: `schedule.stops.${index}.stop_address_type` });
  const watchedStopAirportCode = useWatch({ control, name: `schedule.stops.${index}.stop_airport_code` });
  const watchedStopAirportName = useWatch({ control, name: `schedule.stops.${index}.stop_airport_name` });
  const watchedStopSeaportCode = useWatch({ control, name: `schedule.stops.${index}.stop_seaport_code` });
  const watchedStopSeaportName = useWatch({ control, name: `schedule.stops.${index}.stop_seaport_name` });
  const lastHydratedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOptionalPortAddressFormEnabled) return;
    if (watchedStopAddressType !== StopAddressType.AIRPORT && watchedStopAddressType !== StopAddressType.SEAPORT) {
      return;
    }
    const code = watchedStopAddressType === StopAddressType.AIRPORT ? watchedStopAirportCode : watchedStopSeaportCode;
    const portName = watchedStopAddressType === StopAddressType.AIRPORT ? watchedStopAirportName : watchedStopSeaportName;
    const query = code || portName;
    if (!query) return;
    if (lastHydratedQueryRef.current === query) return;
    lastHydratedQueryRef.current = query;
    const endpoint = watchedStopAddressType === StopAddressType.AIRPORT ? "/common/airport" : "/common/seaport";
    getAdressTypeOption(endpoint, query)
      .then((res) => {
        const first = res?.data?.results?.[0];
        if (!first) return;
        const hasDraftSelectedAddress =
          Boolean(getValues(`schedule.stops.${index}.address_reference_number`)) ||
          Boolean(getValues(`schedule.stops.${index}.address_id`));
        handleFillPortAddressOption(
          [
            {
              id: first.id,
              label: first.name,
              value: first.name,
              city_name: first.city_name,
              country_name: first.country_name,
              country_code: first.country_code,
              code: first.un_locode || first.code,
              address: first.address,
            },
          ],
          { preserveSelectedAddress: hasDraftSelectedAddress },
        );
      })
      .catch(() => {
        // best-effort hydration; allow retry on next value change
        lastHydratedQueryRef.current = null;
      });
  }, [
    isOptionalPortAddressFormEnabled,
    index,
    watchedStopAddressType,
    watchedStopAirportCode,
    watchedStopAirportName,
    watchedStopSeaportCode,
    watchedStopSeaportName,
    handleFillPortAddressOption,
  ]);

  return (
    <>
      {!isAddress && (
        <div className="w-192 my-lg">
          <SearchSelect
            preselect={false}
            testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_PORT_SELECT_PREFIX}-${index}`}
            name={isAirport ? `schedule.stops.${index}.stop_airport_name` : `schedule.stops.${index}.stop_seaport_name`}
            required={isAirport ? "Please select a valid airport" : "Please select a valid port"}
            options={isAirport ? airportsData : seaportsData}
            placeholder={
              isAirport
                ? intl.formatMessage({
                    id: "new-request.schedule.autoCompletePlaceholder.searchAirport",
                    defaultMessage: "Search airport",
                  })
                : intl.formatMessage({
                    id: "new-request.schedule.autoCompletePlaceholder.searchPort",
                    defaultMessage: "Search port",
                  })
            }
            filterBy={["label", "city_name", "country_name", "code"]}
            handleRenderOption={handleRenderAddressOption}
            onInputChange={(val: string) => {
              if (!val?.trim()) {
                clearSelectedPort();
                clearSelectedAddress();
              }
              if (isAirport) {
                handleAirportInputChange(val);
              } else {
                handleSeaportInputChange(val);
              }
            }}
            onSelect={handleFillPortAddressOption}
          />
        </div>
      )}
    </>
  );
}
